import React, { useEffect, useMemo, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { Color } from "Components/Form/Button";
import { useTranslation } from "react-i18next";
import { FiTrash2 } from "react-icons/fi";
import { Prompt, useHistory, useParams } from "react-router-dom";
import { calculateDelayedDays } from "Util/calculateDelayedDays";
import * as yup from "yup";

import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import UITabMaterial, { PanelProps } from "Components/Generic/UITabMaterial";
import LoadingModal from "Components/LoadingModal";
import { SkeletonDetails } from "Components/Shipments/Maintenance/Details";
import { PRIVATE_ROUTES } from "../../../../../constants";
import useYupValidation from "../../../../../hooks/useYupValidation";
import { Waybill } from "Models/waybill";
import { CustomerDetails } from "Models/shipments/shipments.tracking";
import { Job } from "Models/job";
import WaybillsService from "Services/waybill.service";
import {
  CompanyProfile,
  ShipmentStatus,
  ShipmentTrackingStatus,
  ShipmentType,
} from "../../../../../util/enums";
import DeleteWaybillModal, { DeleteWaybillHandle } from "../components/Modal";
import { useActions } from "../hooks/useAction";
import { WaybillsDetails } from "./components/Details";
import WaybillDocuments from "./components/Documents";
import { WaybillNotificationsTab } from "./components/Notifications";
import Orders from "./components/Orders";
import WaybillTitle from "./components/Title";
import WaybillTrackingMap from "./components/TrackingMap";
import { ShipmentProvider } from "./context/useContext";
import { useShipmentContext } from "./hooks/useContext";
import { useShipmentContext as useShipmentContextGeneral } from "Components/Shipments/hooks/useShipmentContext";
import useStyles from "./styled";
import { useAppState } from "Context/StateContext";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceCanEnum, ResourceName } from "Util/enums/accessRule";

import { calculateUtilization } from "Util/calculateUtilization";
import History, { Item } from "Components/Generic/History";
import { ShipmentHistory } from "Models/history";
import { format } from "date-fns";
import { capitalizeFirstLetter } from "Util/capitalizeFirstLetter";
import useActionShipment from "Hooks/useActionShipment";
import { validateWaybillForPublish } from "./validation/validateWaybillForPublish";

enum TAB_SELECTED {
  DETAILS = "details",
  ORDERS = "orders",
  DOCUMENTS = "documents",
  VOYAGE = "voyage",
  NOTIFICATIONS = "notifications",
}

export type ShipmentMaintenanceVariant = "page" | "orderDraft";

export type ShipmentMaintenanceProps = {
  variant?: ShipmentMaintenanceVariant;
  onOrderDraftConfirm?: (waybill: Waybill) => void;
  onOrderDraftCancel?: () => void;
};

export const ShipmentMaintenance = (props: ShipmentMaintenanceProps) => {
  const {
    variant = "page",
    onOrderDraftConfirm,
    onOrderDraftCancel,
  } = props;
  const isOrderDraft = variant === "orderDraft";

  const { t: translate } = useTranslation();
  const history = useHistory();
  const classes = useStyles();
  const { waybillId } = useParams<{ waybillId?: string | undefined }>();

  useEffect(() => {
    if (!isOrderDraft && !waybillId) {
      history.replace(PRIVATE_ROUTES.SHIPMENTS);
    }
  }, [history, isOrderDraft, waybillId]);

  const deleteWaybillModalRef = useRef<DeleteWaybillHandle>(null);

  const {
    shipment,
    isLoading: isFetching,
    hasUnsavedChanges,
    setShipment,
    setHasUnsavedChanges,
  } = useShipmentContext();

  const {
    useCreateWaybill,
    useUpdateWaybill,
    useSubscribeWaybill,
    useCreateWaybillAndSubscribe,
    isSubscribing,
  } = useActions();

  const shipmentId = useMemo(() => {
    if (shipment?.id != null && shipment.id > 0) return shipment.id;

    if (waybillId) return Number(waybillId);

    return undefined;
  }, [shipment?.id, waybillId]);

  const { getShipmentHistories } = useActionShipment();
  const { data: histories = [], isFetching: isFetchingHistories } =
    getShipmentHistories(shipmentId);

  const [hasBeenDeleted, setHasBeenDeleted] = useState<boolean>(false);

  const [hasBeenSaved, setHasBeenSaved] = useState<boolean>(false);
  const [hasBeenSavedForSubscription, setHasBeenSavedForSubscription] = useState<boolean>(false);
  const { currentUser, userPermissions } = useAppState();
  const [shipmentPermission, setShipmentPermission] = useState<{
    edit: boolean;
    create: {
      draft: boolean;
      publish: boolean;
    };
    delete: boolean;
  }>({
    edit: false,
    create: {
      draft: false,
      publish: false,
    },
    delete: false,
  });

  const { mutate: createWaybill, isLoading: isCreating } = useCreateWaybill();
  const { mutate: createAndSubscribeWaybill, isLoading: isCreatingAndSubscribing } =
    useCreateWaybillAndSubscribe();
  const { mutate: updateWaybill, isLoading: isUpdating, isSuccess } = useUpdateWaybill();
  const { isFetching: fetchingWaybill } = useQuery(
    ["waybills.get", waybillId],
    () =>
      WaybillsService.getById(Number(waybillId), {
        include: JSON.stringify([
          {
            association: "jobs",
            include: [
              { association: "itemsJob" },
              { association: "customer" },
              { association: "moveCoordinator" },
              { association: "bookingAgent" },
              { association: "user" },
              { association: "manager" },
              { association: "origins" },
              { association: "destinations" },
            ],
          },
          { association: "containers" },
          { association: "milestones", include: [{ association: "milestone", required: true }] },
          { association: "shipmentTracking", include: [{ association: "vesselPosition" }] },
          {
            association: "shipmentAgents",
            include: [{ association: "agent" }],
          },
          {
            association: "customersToShipment",
            include: [{ association: "customer" }],
          },
          {
            association: "documents",
          },
        ]),
      }),
    {
      enabled: !!waybillId && !isOrderDraft,
      onSuccess: (response) => {
        if (response && response?.data) setShipment(response.data);
      },
    }
  );

  const isLoading = isCreating || isUpdating;
  const isSubscribed = isCreatingAndSubscribing || isSubscribing;

  const [errors, setErrors] = useState<yup.ValidationError[]>();
  const { fieldHasError } = useYupValidation(errors || []);

  const [isPublishReady, setIsPublishReady] = useState(false);

  useEffect(() => {
    if (!isOrderDraft || !shipment) {
      setIsPublishReady(false);
      return;
    }
    const result = validateWaybillForPublish(shipment, translate, {
      companyProfileInitials: currentUser?.company?.profile?.initials,
    });
    setIsPublishReady(result.ok);
  }, [isOrderDraft, shipment, translate, currentUser?.company?.profile?.initials]);

  const schema = yup.object().shape({
    isSubscribed: yup.boolean(),
    shipmentType: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    containerId: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    loadType: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    jobs: yup
      .array()
      .of(yup.object().nullable())
      .when("isSubscribed", {
        is: true,
        then: yup
          .array()
          .of(yup.object().nullable())
          .test("jobs-or-customers-required", translate("thisFieldIsRequired"), function (value) {
            const customers = this.parent.customers;
            const hasJobs = value && value.length > 0;
            const hasCustomers = customers && customers.length > 0;

            if (hasJobs || hasCustomers) return true;

            return false;
          }),
        otherwise: yup.array().notRequired().nullable(),
      }),
    customers: yup
      .array()
      .of(yup.object().nullable())
      .when("isSubscribed", {
        is: true,
        then: yup
          .array()
          .of(yup.object().nullable())
          .test("jobs-or-customers-required", translate("thisFieldIsRequired"), function (value) {
            const jobs = this.parent.jobs;
            const hasJobs = jobs && jobs.length > 0;
            const hasCustomers = value && value.length > 0;

            if (hasJobs || hasCustomers) return true;

            return false;
          }),
        otherwise: yup.array().notRequired().nullable(),
      }),
    containers: yup.array().when("isSubscribed", {
      is: true,
      then: yup
        .array()
        .min(1)
        .of(
          yup.object().shape({
            container: yup
              .string()
              .required(translate("thisFieldIsRequired"))
              .typeError(translate("thisFieldIsRequired")),
            size: yup.string().notRequired().nullable(),
            seal: yup.string().notRequired().nullable(),
            verifiedGrossWeight: yup.boolean().notRequired().nullable(),
            grossWeight: yup
              .number()
              .notRequired()
              .nullable()
              .test("is-required", translate("enterAValueGraterThanNetWeight"), function (value) {
                if (!value && !this.parent.netWeight) return true;

                if (
                  value !== undefined &&
                  value !== null &&
                  this.parent.netWeight !== undefined &&
                  value > this.parent.netWeight
                ) {
                  return true;
                }

                return false;
              }),
            netWeight: yup
              .number()
              .notRequired()
              .nullable()
              .test("is-required", translate("enterAValueLowerThanGrossWeight"), function (value) {
                if (!value && !this.parent.grossWeight) return true;

                if (
                  value !== undefined &&
                  value !== null &&
                  this.parent.grossWeight &&
                  value < this.parent.grossWeight
                ) {
                  return true;
                }

                return false;
              }),
            tareWeight: yup.number().notRequired().nullable(),
            volumetricWeight: yup.number().notRequired().nullable(),
          })
        )
        .test("is-required", translate("thisFieldIsRequired"), function (value) {
          if (value && value.length) return true;

          if (!hasBeenSavedForSubscription) {
            NotificationToast.error(translate("youShouldAddAtLeastOneContainerToTheShipment"), {
              id: "add-container-to-shipment",
            });
          }

          return false;
        }),
      otherwise: yup.array().notRequired().nullable(),
    }),
  });

  const airSchema = yup.object().shape({
    commodity:
      currentUser?.company?.profile?.initials === CompanyProfile.MV
        ? yup.string().required()
        : yup.string().notRequired(),
    isSubscribed: yup.boolean(),
    shipmentType: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    jobs: yup
      .array()
      .of(yup.object().nullable())
      .when("isSubscribed", {
        is: true,
        then: yup
          .array()
          .of(yup.object().nullable())
          .test("jobs-or-customers-required", translate("thisFieldIsRequired"), function (value) {
            const customers = this.parent.customers;
            const hasJobs = value && value.length > 0;
            const hasCustomers = customers && customers.length > 0;

            if (hasJobs || hasCustomers) return true;

            return false;
          }),
        otherwise: yup.array().notRequired().nullable(),
      }),
    customers: yup
      .array()
      .of(yup.object().nullable())
      .when("isSubscribed", {
        is: true,
        then: yup
          .array()
          .of(yup.object().nullable())
          .test("jobs-or-customers-required", translate("thisFieldIsRequired"), function (value) {
            const jobs = this.parent.jobs;
            // At least one must be present: jobs OR customers
            const hasJobs = jobs && jobs.length > 0;
            const hasCustomers = value && value.length > 0;

            // Valid if at least one has values
            if (hasJobs || hasCustomers) return true;

            // Invalid if both are empty
            return false;
          }),
        otherwise: yup.array().notRequired().nullable(),
      }),
  });

  const onValidateAirSchema = (shipment?: Waybill, isSubscribed?: boolean) => {
    try {
      airSchema.validateSync(
        {
          isSubscribed,
          commodity: shipment?.commodity,
          containerId: shipment?.containerId,
          shipmentType: shipment?.shipmentType,
          jobs: shipment?.jobs,
          customers: shipment?.customers,
        },
        { abortEarly: false }
      );

      setErrors([]);

      return true;
    } catch (error: any) {
      if (error instanceof yup.ValidationError) {
        setErrors(error.inner);
      }

      return false;
    }
  };

  const onValidate = (shipment?: Waybill, isSubscribed?: boolean) => {
    try {
      schema.validateSync(
        {
          isSubscribed,
          containerId: shipment?.containerId,
          shipmentType: shipment?.shipmentType,
          loadType: shipment?.loadType,
          jobs: shipment?.jobs,
          containers: shipment?.containers,
          customers: shipment?.customers || shipment?.customersToShipment,
        },
        { abortEarly: false }
      );

      setErrors([]);

      return true;
    } catch (error: any) {
      if (error instanceof yup.ValidationError) {
        console.log("errors: ", error.inner);
        setErrors(error.inner);
      }

      return false;
    }
  };

  const onSave = () => {
    setHasBeenSaved(true);
    setHasUnsavedChanges(false);
    if (!shipment) return;

    if (shipment?.shipmentType?.toLowerCase() === ShipmentType.AIR.toLowerCase()) {
      if (!onValidateAirSchema(shipment)) return;
    } else {
      if (!onValidate(shipment, !!shipment?.marineTrafficId)) return;
    }

    if (isOrderDraft) {
      onOrderDraftConfirm?.({ ...shipment, isDraft: true });
      return;
    }

    if (shipment?.id)
      updateWaybill({
        id: shipment?.id,
        waybill: { ...shipment, isDraft: true },
      });
    else if (shipment) createWaybill({ ...shipment, isDraft: true });
  };

  const onOrderDraftCreate = () => {
    setHasBeenSaved(true);
    setHasUnsavedChanges(false);
    if (!shipment) return;

    if (shipment?.shipmentType?.toLowerCase() === ShipmentType.AIR.toLowerCase()) {
      if (!onValidateAirSchema(shipment)) return;
    } else {
      if (!onValidate(shipment, !!shipment?.marineTrafficId)) return;
    }

    onOrderDraftConfirm?.({ ...shipment, isDraft: false });
  };

  const onSaveAndSubscribe = () => {
    setHasBeenSavedForSubscription(true);
    setHasUnsavedChanges(false);

    if (!shipment) return;

    const publishResult = validateWaybillForPublish(shipment, translate, {
      companyProfileInitials: currentUser?.company?.profile?.initials,
    });

    if (!publishResult.ok) {
      if (publishResult.reason === "no_jobs_or_customers") {
        NotificationToast.error(translate("youShouldAddAtLeastOneOrderToTheShipment"), {
          id: "add-order-to-shipment",
        });
        return;
      }
      if (publishResult.errors?.length) setErrors(publishResult.errors);
      return;
    }

    if (shipment?.shipmentType?.toLowerCase() === ShipmentType.AIR.toLowerCase()) {
      shipment.containers = [];
    }

    if (shipment?.id)
      useSubscribeWaybill({
        id: shipment.id,
        waybill: {
          ...shipment,
          isDraft: false,
        },
      });
    else {
      createAndSubscribeWaybill({ ...shipment, isDraft: false });
    }
  };

  const handleDeleteWaybill = () => {
    setHasBeenDeleted(true);
    updateWaybill({
      id: Number(shipment?.id),
      waybill: {
        ...shipment,
        statusShipment: ShipmentStatus.CANCELLED,
        marineTrafficId: null,
        shipmentTrackingId: null,
      },
    });
  };

  const getItemsToHistory = () => {
    if (!shipment?.id) {
      const firstHistoryIp = (
        histories.find((h: ShipmentHistory) => h?.userIpAddress)?.userIpAddress ?? ""
      ).trim();
      return [
        {
          title: translate("shipmentCreated"),
          activityDate: `${format(new Date(), "yyyy/MM/dd hh:mmbb")}`,
          activityUser: `${currentUser?.name} ${currentUser?.lastName}`,
          activityUserIp: firstHistoryIp,
        } as Item,
      ];
    }

    if (histories && histories.length > 0) {
      return histories.map((history: ShipmentHistory) => {
        const user = history?.user
          ? `${history?.user?.name} ${history?.user?.lastName ? history?.user?.lastName : ""}`
          : "";

        return {
          title: capitalizeFirstLetter(history?.action ?? ""),
          activityDate: history?.updatedAt
            ? `${format(new Date(history?.updatedAt), "yyyy/MM/dd hh:mmbb")}`
            : "",
          activityUser: user,
          activityUserIp: history.userIpAddress ?? "",
        } as Item;
      });
    }
  };

  const activityPanel: PanelProps = {
    menuItem: translate("activity"),
    key: "history",
    render: () => <History items={getItemsToHistory()} isModal />,
  };

  const activityPanels = [activityPanel];

  const actionButtonProps = {
    inactiveDisabled: isLoading,
    inactiveIcon: <FiTrash2 size={12} color="#fff" />,
    textInactive: translate("cancelShipment"),
    onInactive:
      shipment?.id && isShowAction(currentUser, shipmentPermission.delete)
        ? () => {
            setHasUnsavedChanges(false);
            deleteWaybillModalRef.current?.onOpenModal(shipment);
          }
        : undefined,
    textSavePublish:
      shipment?.id &&
      !shipment?.marineTrafficId &&
      shipment.statusShipment?.toLowerCase() !== ShipmentTrackingStatus.DRAFT
        ? translate("update")
        : translate("create"),
    dataCySave: "save-button",
    customColorSavePublish: Color.SECONDARY_DARK,
    customColorSave: shipment?.marineTrafficId ? Color.SUCCESS : Color.SUCCESS_OUTLINED,

    dataCySavePublish: "save-and-subscribe-button",
    savePublishDisabled: isLoading || isSubscribing,
    cancelDisabled: isLoading,
    saveDisabled: isLoading || isSubscribing,
    onCancel: () => {
      if (hasBeenSaved && hasUnsavedChanges) {
        setHasUnsavedChanges(false);
      }
      history.push(PRIVATE_ROUTES.SHIPMENTS);
    },
    onSave,
    onSavePublish: () => onSaveAndSubscribe(),
  };

  const isAccessDeny = (): boolean => {
    if (isOrderDraft) return false;

    return (
      (shipment?.id && !isShowAction(currentUser, shipmentPermission.edit)) ||
      (!shipment?.id &&
        !isShowAction(currentUser, shipmentPermission.create.publish) &&
        !isShowAction(currentUser, shipmentPermission.create.draft))
    );
  };

  const detailsPanel: PanelProps = {
    key: TAB_SELECTED.DETAILS,
    className: classNames(classes.uiTab, classes.tabDetails),
    menuItem: translate("details"),
    loading: isFetching || fetchingWaybill,
    hasError:
      shipment?.shipmentType !== ShipmentType.AIR
        ? fieldHasError("shipmentType")
        : fieldHasError("containerNumbers") || fieldHasError("loadType"),
    skeleton: () => (
      <div style={{ padding: "24px 32px" }}>
        <SkeletonDetails />
      </div>
    ),
    render: () => {
      if (isAccessDeny()) return null;
      return (
        <WaybillsDetails
          permission={{
            delete: isShowAction(currentUser, shipmentPermission.delete),
          }}
          isLoading={isLoading}
          isSubscribing={isSubscribed}
          onSave={
            (isOrderDraft || isShowAction(currentUser, shipmentPermission.create.draft)) &&
            !shipment?.shipmentTrackingId
              ? onSave
              : undefined
          }
          errors={errors}
          hasBeenSaved={hasBeenSaved || hasBeenSavedForSubscription}
          setOpenConfirmationDeleteModal={
            isShowAction(currentUser, shipmentPermission.delete)
              ? () => shipment && deleteWaybillModalRef.current?.onOpenModal(shipment)
              : undefined
          }
          onSaveAndSubscribe={
            isOrderDraft
              ? onOrderDraftCreate
              : isShowAction(currentUser, shipmentPermission.create.publish)
                ? () => {
                    onSaveAndSubscribe();
                  }
                : undefined
          }
          publishDisabled={isOrderDraft ? !isPublishReady : undefined}
          onCancelOverride={isOrderDraft ? onOrderDraftCancel : undefined}
        />
      );
    },
  };

  const ordersPanel: PanelProps = {
    key: TAB_SELECTED.ORDERS,
    className: classNames(classes.uiTab, classes.tabDetails),
    menuItem: translate("orders"),
    loading: isFetching || fetchingWaybill,
    hasError: fieldHasError("jobs") || fieldHasError("customers"),
    render: () => {
      if (isAccessDeny()) return null;
      return (
        <Orders
          permission={{
            delete: isShowAction(currentUser, shipmentPermission.delete),
          }}
          isSaving={isLoading}
          isSubscribing={isSubscribed}
          hasBeenSaved={hasBeenSaved || hasBeenSavedForSubscription}
          orders={shipment?.jobs || []}
          customers={shipment?.customersToShipment || []}
          onSave={
            isShowAction(currentUser, shipmentPermission.create.draft) &&
            !shipment?.shipmentTrackingId
              ? onSave
              : undefined
          }
          onSaveAndSubscribe={
            !isOrderDraft && isShowAction(currentUser, shipmentPermission.create.publish)
              ? () => onSaveAndSubscribe()
              : undefined
          }
          setOpenConfirmationDeleteModal={
            isShowAction(currentUser, shipmentPermission.delete)
              ? () => shipment && deleteWaybillModalRef.current?.onOpenModal(shipment)
              : undefined
          }
        />
      );
    },
  };

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  const documentsPanel: PanelProps = {
    key: TAB_SELECTED.DOCUMENTS,
    className: classNames(classes.uiTab, classes.tabDetails),
    menuItem: translate("documents"),
    loading: isFetching || fetchingWaybill,
    skeleton: () => <SkeletonDetails />,
    render: () => (
      <WaybillDocuments
        waybillDoc={shipment?.documents}
        orders={shipment?.jobs}
        actionButtonProps={actionButtonProps}
        onSetWaybill={(docs) => setShipment({ ...(shipment || {}), documents: docs })}
      />
    ),
  };

  const liveTrackingPanel: PanelProps = {
    key: TAB_SELECTED.VOYAGE,
    className: classNames(classes.uiTab, classes.tabDetails),
    menuItem: translate("tracking"),
    loading: isFetching || fetchingWaybill,
    render: () => <WaybillTrackingMap actionButtonProps={actionButtonProps} />,
  };

  const notificationsPanel: PanelProps = {
    key: TAB_SELECTED.NOTIFICATIONS,
    className: classNames(classes.uiTab, classes.tabDetails),
    menuItem: translate("notifications"),
    loading: isFetching || fetchingWaybill,
    skeleton: () => <SkeletonDetails />,
    render: () => {
      if (isAccessDeny()) return null;
      return (
        <WaybillNotificationsTab
          permission={{
            delete: isShowAction(currentUser, shipmentPermission.delete),
          }}
          isSaving={isLoading}
          hasBeenSaved={hasBeenSaved || hasBeenSavedForSubscription}
          onSave={
            isShowAction(currentUser, shipmentPermission.create.publish) &&
            !shipment?.shipmentTrackingId
              ? onSave
              : undefined
          }
          onSaveAndSubscribe={
            isOrderDraft
              ? onOrderDraftCreate
              : isShowAction(currentUser, shipmentPermission.create.publish)
                ? () => onSaveAndSubscribe()
                : undefined
          }
          publishDisabled={isOrderDraft ? !isPublishReady : undefined}
          setOpenConfirmationDeleteModal={
            isShowAction(currentUser, shipmentPermission.delete)
              ? () => shipment && deleteWaybillModalRef.current?.onOpenModal(shipment)
              : undefined
          }
        />
      );
    },
  };

  const leftPanelsFull =
    shipment?.shipmentTracking?.shipmentStatus &&
    shipment?.shipmentTracking?.shipmentStatus?.toLowerCase() !==
      ShipmentStatus.WAITING_FOR_UPDATE.toLowerCase()
      ? [detailsPanel, ordersPanel, notificationsPanel, liveTrackingPanel]
      : [detailsPanel, ordersPanel, notificationsPanel];

  const leftPanels = isOrderDraft
    ? leftPanelsFull.filter((p) => p.key !== TAB_SELECTED.ORDERS)
    : leftPanelsFull;

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setShipmentPermission({
        create: {
          draft:
            checkResourceCan(userPermissions, ResourceName.SHIPMENTS, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_SHIPMENTS_DRAFT),
          publish:
            checkResourceCan(userPermissions, ResourceName.SHIPMENTS, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_SHIPMENTS_PUBLISH),
        },
        edit:
          checkResourceCan(userPermissions, ResourceName.SHIPMENTS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_SHIPMENTS_EDIT),
        delete:
          checkResourceCan(userPermissions, ResourceName.SHIPMENTS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_SHIPMENTS_DELETE),
      });
    }
  }, [userPermissions]);

  useEffect(() => {
    if (hasBeenSavedForSubscription) {
      if (shipment?.shipmentType !== ShipmentType.AIR) onValidate(shipment, true);
      else onValidateAirSchema(shipment, true);
    }
  }, [hasBeenSavedForSubscription, shipment]);

  useEffect(() => {
    if (hasBeenSaved) {
      if (shipment?.shipmentType !== ShipmentType.AIR)
        onValidate(shipment, !!shipment?.marineTrafficId);
      else onValidateAirSchema(shipment, false);
    }
  }, [hasBeenSaved, shipment]);

  useEffect(() => {
    if (hasBeenDeleted && isSuccess && !isOrderDraft) {
      history.push(PRIVATE_ROUTES.SHIPMENTS);
    }
  }, [isSuccess, hasBeenDeleted, isOrderDraft, history]);

  useEffect(() => {
    setShipment({
      ...shipment,
      delayed: calculateDelayedDays(
        shipment?.shipmentTracking?.finalPortPredictiveArrival,
        shipment?.shipmentTracking?.finalPortPredictiveArrivalLast
      ),
    });
  }, [
    shipment?.shipmentTracking?.finalPortPredictiveArrival,
    shipment?.shipmentTracking?.finalPortPredictiveArrivalLast,
  ]);

  useEffect(() => {
    setShipment({
      ...shipment,
      utilization: calculateUtilization(shipment?.totalVolume || 0, shipment?.addedVolume || 0),
    });
  }, [shipment?.jobs, shipment?.containers, shipment?.totalVolume, shipment?.addedVolume]);

  if (!isOrderDraft && !waybillId) {
    return null;
  }

  return (
    <React.Fragment>
      {!isOrderDraft && (
        <WaybillTitle
          isEditing={!!waybillId}
          shipmentId={shipment?.containerId}
          hasMarineTrafficId={!!shipment?.marineTrafficId}
          status={
            shipment?.shipmentTrackingId
              ? shipment.shipmentTracking?.shipmentStatus
              : shipment?.statusShipment
          }
        />
      )}
      <div className={classes.mainDiv}>
        <Prompt when={hasUnsavedChanges && !isOrderDraft} message="" />
        {isOrderDraft ? (
          <div data-cy="shipment-order-draft-inline">{detailsPanel.render?.()}</div>
        ) : (
          <UITabMaterial
            useInitialPadding
            showActivityButton
            activityPanels={activityPanels}
            leftPanels={leftPanels}
          />
        )}

        <DeleteWaybillModal
          ref={deleteWaybillModalRef}
          onDelete={handleDeleteWaybill}
          isDeleting={isUpdating}
        />
      </div>
      <LoadingModal showing={isLoading || isSubscribing} />
    </React.Fragment>
  );
};

export function ShipmentMovingMaintenancePage() {
  return (
    <ShipmentProvider>
      <ShipmentMaintenance />
    </ShipmentProvider>
  );
}
